/** Client portal: payroll — the next pay date, hours and approval, changes and new hires, cost trend, filed forms. */
import { formatCents } from "@clientflow/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CalendarClock, CheckCircle2, FileText, UserPlus } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ChangeForm } from "@/components/payroll";
import { Empty, Loading, PageHeader, Section } from "@/components/page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errorMessage } from "@/lib/api";
import { daysBetween, formatDate, plural } from "@/lib/format";
import { cn } from "@/lib/utils";

type PortalPayroll = {
  today: string;
  changeKinds: Record<string, string>;
  clients: Array<{
    clientId: string;
    provider: string;
    next: { id: string; payDate: string; cutoffOn: string; status: string; clientApproves: boolean; hoursNote: string } | null;
    toApprove: Array<{ id: string; payDate: string; cutoffOn: string; grossCents: number | null; netCents: number | null; employerTaxesCents: number | null; headcount: number | null; notes: string }>;
    costTrend: Array<{ month: string; cents: number }>;
    changes: Array<{ id: string; kind: string; employeeName: string; effectiveOn: string | null; status: string; createdAt: string }>;
    filed: Array<{ id: string; form: string; periodLabel: string; filedOn: string | null; documentId: string | null }>;
  }>;
};

function HoursForm({ clientId, runId, existing }: { clientId: string; runId: string; existing: string }) {
  const [note, setNote] = useState(existing);
  const [sent, setSent] = useState(false);
  useEffect(() => setNote(existing), [existing]);
  const file = useRef<HTMLInputElement>(null);
  const send = useMutation({
    meta: { inlineError: true },
    mutationFn: async () => {
      const f = file.current?.files?.[0];
      const doc = f ? await api.upload<{ id: string }>("/api/portal/documents", { clientId, folder: "Payroll" }, f) : null;
      return api.send("POST", `/api/portal/payroll/runs/${runId}/hours`, { note, documentId: doc?.id ?? null });
    },
    onSuccess: () => {
      setSent(true);
      // The timesheet went with it: sending again doesn't upload it twice.
      if (file.current) file.current.value = "";
    },
  });
  return (
    <form
      className="mt-4 space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        send.mutate();
      }}
    >
      <Label htmlFor={`hr-${runId}`}>Hours and changes for this payroll</Label>
      <Textarea id={`hr-${runId}`} rows={3} required placeholder="e.g. Jamie 72 hours; Lee 80 hours plus a $200 bonus; Sam was off" value={note}
        onChange={(e) => {
          setNote(e.target.value);
          setSent(false);
        }}
      />
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <Input ref={file} type="file" aria-label="Timesheet (optional)" className="sm:w-72" />
        <Button type="submit" disabled={send.isPending}>
          {existing ? "Update hours" : "Send hours"}
        </Button>
        {sent && <span className="text-sm text-emerald-700 dark:text-emerald-300">Sent</span>}
      </div>
      {send.error && <p className="text-sm text-destructive">{errorMessage(send.error)}</p>}
    </form>
  );
}

function ClientPayroll({ c, today, kinds, name }: { c: PortalPayroll["clients"][number]; today: string; kinds: Record<string, string>; name?: string }) {
  const [changing, setChanging] = useState(false);
  const approve = useMutation({ meta: { inlineError: true }, mutationFn: (id: string) => api.send("POST", `/api/portal/payroll/runs/${id}/approve`) });
  const n = c.next;
  const days = n ? daysBetween(today, n.cutoffOn) : 0;
  return (
    <div className="space-y-6">
      {name && <h2 className="text-lg font-semibold">{name}</h2>}
      <div className="grid gap-6 lg:grid-cols-2">
        <Section title="Next payroll">
          {!n ? (
            <p className="text-sm text-muted-foreground">No upcoming pay date is scheduled.</p>
          ) : (
            <>
              <div className="flex items-start gap-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <CalendarClock className="h-6 w-6" />
                </span>
                <div>
                  <p className="text-2xl font-semibold">{formatDate(n.payDate)}</p>
                  <p className={cn("text-sm", days <= 1 ? "font-medium text-amber-700 dark:text-amber-300" : "text-muted-foreground")}>
                    {days < 0 ? "The deadline to send changes has passed; message us right away." : days === 0 ? "Send hours and changes today." : `Send hours and changes within ${days} day${days === 1 ? "" : "s"} (by ${formatDate(n.cutoffOn)}).`}
                  </p>
                </div>
              </div>
              {["upcoming", "hours_in"].includes(n.status) ? (
                <HoursForm clientId={c.clientId} runId={n.id} existing={n.hoursNote} />
              ) : (
                <p className="mt-4 text-sm text-muted-foreground">{n.status === "ready" ? "Ready for your approval below." : n.status === "approved" ? "Approved. We'll process it on time." : "Processed."}</p>
              )}
            </>
          )}
        </Section>
        <Section title="Payroll cost" description="Gross pay plus employer taxes, by month.">
          {!c.costTrend.length ? (
            <p className="text-sm text-muted-foreground">Appears once payrolls are processed.</p>
          ) : (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={c.costTrend.map((x) => ({ month: new Date(`${x.month}-01T12:00:00Z`).toLocaleString("en-US", { month: "short", timeZone: "UTC" }) + (x.month === today.slice(0, 7) ? " (to date)" : ""), cost: x.cents / 100 }))}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tickLine={false} axisLine={false} fontSize={12} />
                  <YAxis tickFormatter={(v) => `$${Math.round(v / 1000)}k`} tickLine={false} axisLine={false} fontSize={12} width={44} />
                  <Tooltip formatter={(v: number) => formatCents(Math.round(v * 100))} />
                  <Bar dataKey="cost" name="Payroll cost" fill="var(--viz-1)" radius={[4, 4, 0, 0]} maxBarSize={28} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Section>
      </div>
      {c.toApprove.length > 0 && (
        <Section title="Approve payroll">
          <ul className="space-y-3">
            {c.toApprove.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-900 dark:bg-amber-950/40">
                <span className="min-w-0 flex-1 text-sm">
                  <span className="block font-medium">Pay date {formatDate(r.payDate)}</span>
                  <span className="text-muted-foreground">
                    {r.headcount !== null && `${plural(r.headcount, "employee")} · `}
                    {r.grossCents !== null && `gross ${formatCents(r.grossCents)} · `}
                    {r.netCents !== null && `net ${formatCents(r.netCents)} · `}
                    approve by {formatDate(r.cutoffOn)}
                  </span>
                  {r.notes && <span className="block text-xs text-muted-foreground">{r.notes}</span>}
                </span>
                <Button onClick={() => approve.mutate(r.id)} disabled={approve.isPending}>
                  <CheckCircle2 className="h-4 w-4" /> Approve
                </Button>
              </li>
            ))}
          </ul>
          {approve.error && <p className="mt-2 text-sm text-destructive">{errorMessage(approve.error)}</p>}
        </Section>
      )}
      <div className="grid gap-6 lg:grid-cols-2">
        <Section
          title="Changes and new employees"
          action={
            !changing && (
              <Button size="sm" variant="outline" onClick={() => setChanging(true)}>
                <UserPlus className="h-4 w-4" /> New employee or change
              </Button>
            )
          }
        >
          {changing && (
            <div className="mb-4 rounded-md border border-border p-3">
              <ChangeForm endpoint="/api/portal/payroll/changes" clientId={c.clientId} onDone={() => setChanging(false)} kinds={kinds} />
            </div>
          )}
          {!c.changes.length ? (
            <p className="text-sm text-muted-foreground">Tell us about new hires, raises, terminations or address and tax changes here.</p>
          ) : (
            <ul className="divide-y divide-border">
              {c.changes.map((x) => (
                <li key={x.id} className="flex items-center gap-3 py-2 text-sm">
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium">{x.employeeName}</span>
                    <span className="text-xs text-muted-foreground">
                      {kinds[x.kind]}
                      {x.effectiveOn && ` · ${formatDate(x.effectiveOn)}`}
                    </span>
                  </span>
                  <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-medium", x.status === "done" ? "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200" : "bg-muted text-muted-foreground")}>{x.status === "done" ? "Done" : x.status === "cancelled" ? "Cancelled" : "We're on it"}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>
        <Section title="Filed payroll forms">
          {!c.filed.length ? (
            <p className="text-sm text-muted-foreground">Copies of filed returns appear here.</p>
          ) : (
            <ul className="divide-y divide-border">
              {c.filed.map((f) => (
                <li key={f.id}>
                  {f.documentId ? (
                    <a href={`/api/portal/documents/${f.documentId}/download`} className="flex items-center gap-3 py-2 text-sm hover:underline">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <span className="flex-1">
                        {f.form} · {f.periodLabel}
                      </span>
                      <span className="text-xs text-muted-foreground">Filed {formatDate(f.filedOn)}</span>
                    </a>
                  ) : (
                    <div className="flex items-center gap-3 py-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
                      <span className="flex-1">
                        {f.form} · {f.periodLabel}
                      </span>
                      <span className="text-xs text-muted-foreground">Filed {formatDate(f.filedOn)}</span>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </div>
  );
}

export function PortalPayrollPage({ clientName }: { clientName: (id: string) => string | undefined }) {
  const { data, isLoading } = useQuery<PortalPayroll>({ queryKey: ["/api/portal/payroll"] });
  return (
    <>
      <PageHeader title="Payroll" description="Your next payroll, approvals, changes and filed payroll forms." />
      {isLoading ? (
        <Loading />
      ) : !data?.clients.length ? (
        <Empty>Payroll isn't set up for you yet.</Empty>
      ) : (
        <div className="space-y-10">
          {data.clients.map((c) => (
            <ClientPayroll key={c.clientId} c={c} today={data.today} kinds={data.changeKinds} name={data.clients.length > 1 ? clientName(c.clientId) : undefined} />
          ))}
        </div>
      )}
    </>
  );
}
